import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CompanyAvatar } from "./CompanyAvatar";
import { BookmarkButton } from "./BookmarkButton";
import { Bookmark } from "lucide-react";
import {
  DollarSign,
  Clock,
  UserPlus,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import {
  isExternalJob,
  getCompanyName,
  getCompanyLogoUrl,
  getExternalApplyUrl,
  getSourceName,
  getSourceLabel,
  withUtm,
} from "@/lib/jobUtils";

interface JobDetailSidebarProps {
  job: any;
  user: any;
  authLoading: boolean;
  existingApplication: any;
  /** Whether this job is bookmarked */
  isSaved?: boolean;
  /** Callback to toggle bookmark */
  onToggleSave?: () => void;
}

export function JobDetailSidebar({ job, user, authLoading, existingApplication, isSaved = false, onToggleSave }: JobDetailSidebarProps) {
  const navigate = useNavigate();
  const companyName = getCompanyName(job);
  const logoUrl = getCompanyLogoUrl(job);
  const isExternal = isExternalJob(job);
  const externalUrl = getExternalApplyUrl(job);
  const sourceName = getSourceName(job);
  const nextParam = `/oportunidades/${job.id}`;

  return (
    <div className="space-y-5">
      {/* Stipend */}
      {(job.stipend_min || job.stipend_max) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-600 dark:text-green-400" />Bolsa-auxílio
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-foreground">
              {job.stipend_max && job.stipend_max !== job.stipend_min
                ? `R$ ${job.stipend_min?.toLocaleString()} – R$ ${job.stipend_max.toLocaleString()}`
                : `R$ ${(job.stipend_min || job.stipend_max)?.toLocaleString()}`}
            </p>
            <p className="text-sm text-muted-foreground">/mês</p>
          </CardContent>
        </Card>
      )}

      {/* CTA */}
      {renderCTA(job, user, authLoading, isExternal, externalUrl, sourceName, existingApplication, nextParam)}

      {/* Save button */}
      {user && onToggleSave ? (
        <BookmarkButton
          isSaved={isSaved}
          onClick={() => {
            onToggleSave();
            if (!isSaved) {
              navigate("/aluno/vagas/salvas");
            }
          }}
          variant="button"
        />
      ) : !user && !authLoading ? (
        <Button variant="outline" size="lg" className="w-full gap-2" asChild>
          <Link to={`/aluno/escolha?next=${encodeURIComponent(nextParam)}`}>
            <Bookmark className="h-4 w-4" />
            Salvar esta vaga
          </Link>
        </Button>
      ) : null}

      {/* Deadline */}
      {job.deadline_date && (
        <Card className="border-orange-200 bg-orange-50 dark:border-orange-900 dark:bg-orange-950/30">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="h-5 w-5 text-orange-500" />
            <div>
              <p className="text-sm font-medium text-foreground">Inscrições até</p>
              <p className="text-sm text-muted-foreground">
                {new Date(job.deadline_date).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* About company */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Sobre a empresa</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 mb-3">
            <CompanyAvatar logoUrl={logoUrl} companyName={companyName} size="sm" />
            <div>
              <p className="font-medium text-foreground">{companyName}</p>
              {job.partner_orgs?.industry && <p className="text-xs text-muted-foreground">{job.partner_orgs.industry}</p>}
            </div>
          </div>
          <Link
            to={`/oportunidades?q=${encodeURIComponent(companyName)}`}
            className="text-xs text-primary hover:underline"
          >
            Ver outras vagas desta empresa →
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}

function renderCTA(
  job: any,
  user: any,
  authLoading: boolean,
  isExternal: boolean,
  externalUrl: string | null,
  sourceName: string | null,
  existingApplication: any,
  nextParam: string,
) {
  if (!user && !authLoading) {
    return (
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-5 text-center space-y-3">
          <UserPlus className="h-8 w-8 text-primary mx-auto" />
          <h3 className="font-semibold text-foreground">Quer se candidatar?</h3>
          <p className="text-sm text-muted-foreground">
            Crie sua conta gratuita para se candidatar a esta e outras oportunidades
          </p>
          <Button asChild className="w-full" size="lg">
            <Link to={`/aluno/escolha?next=${encodeURIComponent(nextParam)}`}>Criar minha conta grátis</Link>
          </Button>
          <p className="text-xs text-muted-foreground">
            Já tem conta?{" "}
            <Link to={`/aluno/login?next=${encodeURIComponent(nextParam)}`} className="text-primary hover:underline">
              Faça login
            </Link>
          </p>
        </CardContent>
      </Card>
    );
  }

  if (isExternal && user) {
    return (
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-5 text-center space-y-3">
          <AlertTriangle className="h-6 w-6 text-accent mx-auto" />
          <h3 className="font-semibold text-foreground">Vaga externa</h3>
          <p className="text-sm text-muted-foreground">
            Você será redirecionado para o {getSourceLabel(sourceName)}. A candidatura é feita diretamente no site da empresa.
          </p>
          <Button className="w-full" size="lg" onClick={() => window.open(withUtm(externalUrl!), "_blank")}>
            Candidatar no {getSourceLabel(sourceName)}
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (user && existingApplication) {
    return (
      <Card className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30">
        <CardContent className="p-5 text-center space-y-2">
          <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto" />
          <h3 className="font-semibold text-foreground">Candidatura enviada</h3>
          <p className="text-sm text-muted-foreground">Você já se candidatou a esta vaga</p>
        </CardContent>
      </Card>
    );
  }

  if (user) {
    return (
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-5 text-center">
          <Button asChild className="w-full" size="lg">
            <Link to={`/aluno/vagas/${job.id}/aplicar`}>Candidatar-me</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return null;
}
